import { Trans } from 'react-i18next'
import clsx from 'clsx'

import { CondArg, ITableQuery, TableQueryOp } from '@makyoapp/makyo-sdk/models/Table.js'
import {
  ColumnAdapter,
  ConditionNumericOperator,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  DefaultConditionOperator,
  FunctionColumnValue,
  NUMERIC_OPERATOR_WITH_INPUT_RHS,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  OPERATOR_WITH_BETWEEN_RHS,
  OPERATOR_WITH_INPUT_RHS,
  TableAdapter,
} from '@makyoapp/makyo-common'
import { Trash16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, Field, SelectDropdown, TextSwitch } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { getOperatorDefaultOption, getOperatorOptions, validateRhsEndValues } from '@/shared/condition'

import { ColumnOrCustomDd } from '../Function/ColumnOrCustomDd'
import { Lbl } from '../GridV2/components/fields/shared'
import { RepeaterButton } from '../RepeaterButton'

interface ConditionItemProps {
  index: number
  colOptions: ColumnAdapter[]
  tables: TableAdapter[]
  table: TableAdapter
  value: ITableQuery
  onChange: (v: ITableQuery) => void
}

function ConditionItem({ index, colOptions, tables, table, value, onChange }: ConditionItemProps) {
  const statement = value._Statements[index]

  const colObj = colOptions.find((c) => c.id === statement._Column)
  const operator = statement._Op as DefaultConditionOperator

  const isDateSingleOperator = DATE_OPERATOR_WITH_RHS.includes(operator)
  const isDateRangeOperator = DATE_OPERATOR_WITH_RANGE_RHS.includes(operator)
  const isDateOperator = isDateSingleOperator || isDateRangeOperator

  function validateRhsValues(opt: DefaultConditionOperator): Record<string, unknown> {
    const extra = validateRhsEndValues(opt)
    return {
      _Value1: extra.rhs ?? {
        isCustom: true,
        value: '',
      },
      _Value2: extra.rhsEnd ?? {
        isCustom: true,
        value: '',
      },
    }
  }

  const selectOptions = colObj
    ? (Object.values(getOperatorOptions(colObj, table, tables)) as DefaultConditionOperator[])
    : []

  return (
    <div className='flex-c min-w-0'>
      <div className='flex-c p-2 border bdr rounded-md bg-zinc-50 dark:bg-slate-700 min-w-0 w-full'>
        <div className='flex flex-col gap-2 min-w-0 w-full'>
          <div className='flex-c w-full'>
            <div className='w-full min-w-0'>
              <ColumnOrCustomDd
                inputClassName='dark:hover:bg-slate-800!'
                inputFocusClassName='dark:bg-slate-800!'
                id={`${index}-lhs`}
                table={table}
                value={{
                  isCustom: false,
                  value: statement._Column || '',
                }}
                options={colOptions}
                onChange={(lhs) => {
                  const lhsCol = lhs.value as string

                  const nextCond = [...value._Statements]

                  const isSame = nextCond[index]._Column === lhsCol
                  const newColObj = colOptions.find((c) => c.id === lhsCol)
                  const currentColObj = colOptions.find((c) => c.id === statement._Column)
                  const shouldReset = newColObj && currentColObj && newColObj.type !== currentColObj.type

                  const nextOp = shouldReset ? getOperatorDefaultOption(newColObj, table, tables) : nextCond[index]._Op

                  let extra: Record<string, unknown> = {}
                  if (shouldReset || !isSame) {
                    extra = validateRhsValues(nextOp as DefaultConditionOperator)
                  }
                  nextCond[index] = {
                    ...nextCond[index],
                    _Column: lhsCol,
                    _Op: nextOp,
                    ...(shouldReset ? extra : {}),
                  }

                  onChange({
                    _Op: value._Op,
                    _Statements: nextCond,
                  })
                }}
                toggleClassName='dark:hover:bg-slate-800!'
                customTypeOnly={false}
                includeUnknown={true}
              />
            </div>
            <div className='w-full min-w-0 flex-shrink-1'>
              <SelectDropdown
                toggleClassName={clsx('dark:hover:bg-slate-800!')}
                value={operator}
                onChange={(opt) => {
                  const nextCond = [...value._Statements]
                  nextCond[index] = {
                    ...nextCond[index],
                    _Op: opt,
                    _Value1: {
                      isCustom: true,
                      value: '',
                    },
                    _Value2: {
                      isCustom: true,
                      value: '',
                    },
                  }
                  onChange({
                    _Op: value._Op,
                    _Statements: nextCond,
                  })
                }}
                className='w-full'
                menuContainerClassName={clsx('min-w-[220px]')}
                portalMenu
                menuClassName='text-m-m dark:text-slate-300'
                menuZIndex='z-402'
                menuFullWidth={false}
                options={selectOptions}
                renderOption={(opt) => {
                  const split = opt.split('-').slice(1).join(' ')
                  return split.charAt(0).toUpperCase() + split.slice(1)
                }}
              />
            </div>
          </div>
          {OPERATOR_WITH_BETWEEN_RHS.includes(operator) && (
            <div className='uppercase text-m-ss'>
              <Trans>Lower Bound</Trans>
            </div>
          )}
          {(OPERATOR_WITH_INPUT_RHS.includes(operator) ||
            DATE_OPERATOR_WITH_RHS.includes(operator) ||
            NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(operator as ConditionNumericOperator) ||
            DATE_OPERATOR_WITH_RANGE_RHS.includes(operator)) && (
            <ColumnOrCustomDd
              withCustomValue
              inputClassName='dark:hover:bg-slate-800!'
              inputFocusClassName='dark:bg-slate-800!'
              withExtraOptions={isDateSingleOperator ? ['date'] : undefined}
              id={`${index}-rhs`}
              table={table}
              value={
                (statement._Value1 as FunctionColumnValue) || {
                  isCustom: true,
                  value: '',
                }
              }
              customValueType={
                isDateOperator ? 'date' : NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(operator) ? 'number' : 'text'
              }
              toggleClassName='dark:hover:bg-slate-800!'
              onChange={(v) => {
                const nextCond = [...value._Statements]
                nextCond[index] = {
                  ...nextCond[index],
                  _Value1: v as CondArg,
                }
                onChange({
                  _Op: value._Op,
                  _Statements: nextCond,
                })
              }}
              excludeColumns={statement._Column ? [statement._Column] : []}
            />
          )}
          {OPERATOR_WITH_BETWEEN_RHS.includes(operator) && (
            <div className='uppercase text-m-ss'>
              <Trans>Upper Bound</Trans>
            </div>
          )}
          {(NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(operator as ConditionNumericOperator) ||
            DATE_OPERATOR_WITH_RANGE_RHS.includes(operator)) && (
            <ColumnOrCustomDd
              inputClassName='dark:hover:bg-slate-800!'
              inputFocusClassName='dark:bg-slate-800!'
              withCustomValue
              withExtraOptions={isDateSingleOperator ? ['date'] : undefined}
              id={`${index}-rhs-end`}
              table={table}
              value={
                (statement._Value2 as FunctionColumnValue) || {
                  isCustom: true,
                  value: '',
                }
              }
              customValueType={
                isDateOperator ? 'date' : NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(operator) ? 'number' : 'text'
              }
              toggleClassName='dark:hover:bg-slate-800!'
              onChange={(v) => {
                const nextCond = [...value._Statements]
                nextCond[index] = {
                  ...nextCond[index],
                  _Value2: v as CondArg,
                }
                onChange({
                  _Op: value._Op,
                  _Statements: nextCond,
                })
              }}
              excludeColumns={statement._Column ? [statement._Column] : []}
            />
          )}
        </div>
      </div>
      <div className='flex flex-col gap-2 items-center'>
        <Button
          variant={ButtonVariant.Ghost}
          icon={<Trash16 />}
          className={clsx('shrink-0 w-6 h-9')}
          onClick={() => {
            const nextCond = [...value._Statements]
            nextCond.splice(index, 1)
            onChange({
              _Op: value._Op,
              _Statements: nextCond,
            })
          }}
          noPadding
        />
      </div>
    </div>
  )
}

interface SnapshotFilterConditionProps {
  value: ITableQuery
  onChange: (queryFilter: ITableQuery) => void
  sourceColumns?: ColumnAdapter[]
  sourceTable?: TableAdapter
  isRequired?: boolean
}

export function SnapshotFilterCondition({
  value,
  onChange,
  sourceColumns = [],
  sourceTable,
  isRequired,
}: SnapshotFilterConditionProps) {
  const { tables } = useDbClientContext()

  return (
    <Field>
      <Lbl className='self-start pt-2' required={isRequired}>
        <Trans>Show Items</Trans>
      </Lbl>

      <div className='flex-1 min-w-0'>
        <div className='flex flex-col gap-4 w-full'>
          <div className='flex-c'>
            <div>
              <Trans>If</Trans>
            </div>
            <div>
              <TextSwitch
                checked={value._Op === TableQueryOp.AND}
                onClick={(isChecked) => {
                  onChange({
                    _Op: isChecked ? TableQueryOp.AND : TableQueryOp.OR,
                    _Statements: value?._Statements ?? [],
                  })
                }}
                textOff={<Trans>Any</Trans>}
                textOn={<Trans>All</Trans>}
                keepActiveColor
              />
            </div>
            <div>
              <Trans>of the following conditions are met</Trans>
            </div>
          </div>

          {value._Statements.length > 0 && (
            <div className='flex gap-2 flex-col w-full'>
              {value._Statements.map((cond, i) => {
                return (
                  <ConditionItem
                    index={i}
                    key={i}
                    colOptions={sourceColumns}
                    tables={tables}
                    table={sourceTable!}
                    value={value}
                    onChange={onChange}
                  />
                )
              })}
            </div>
          )}

          <RepeaterButton
            disabled={sourceColumns.length < 1 || !sourceTable}
            onClick={() => {
              const firstColumn = sourceColumns[0]

              onChange({
                _Op: TableQueryOp.AND,
                _Statements: [
                  ...(value?._Statements ?? []),
                  {
                    _Column: firstColumn.id,
                    _Op: getOperatorDefaultOption(firstColumn, sourceTable!, tables),
                    _Value1: {
                      isCustom: true,
                      value: '',
                    },
                    _Value2: {
                      isCustom: true,
                      value: '',
                    },
                    Options: null as any,
                  },
                ],
              })
            }}
          >
            <Trans>Add Condition</Trans>
          </RepeaterButton>
        </div>
      </div>
    </Field>
  )
}
